import { TopNav } from './components/Laurel/TopNav'
import { Footer } from './components/Laurel/Footer'
import { Metric } from './components/shared/Metric'
import { Sparkline } from './components/shared/Sparkline'
import { demoMetricsConfig, metricsConfig } from './config'
import { formatCurrency, formatMonthlyGrowth, formatRate, formatInteger, formatTimestamp } from './utils/formatters'

export function OpenPage() {
  const timestamp = formatTimestamp()
  const metrics = metricsConfig.enabled.map((metricKey) => {
    switch (metricKey) {
      case 'mrr':
        return {
          key: metricKey,
          label: 'Monthly Recurring Revenue',
          value: formatCurrency(demoMetricsConfig.metrics.mrr),
          sub: formatMonthlyGrowth(demoMetricsConfig.metrics.mom),
          meta: `${demoMetricsConfig.note} • Updated ${timestamp}`
        }
      case 'simple_churn':
        return {
          key: metricKey,
          label: 'Simple Churn',
          value: formatRate(demoMetricsConfig.metrics.simple_churn),
          sub: 'Goal < 5%',
          meta: `${demoMetricsConfig.note} • Updated ${timestamp}`
        }
      case 'customer_count':
        return {
          key: metricKey,
          label: 'Customers',
          value: formatInteger(demoMetricsConfig.metrics.customers),
          sub: '+12 new this month',
          meta: `${demoMetricsConfig.note} • Updated ${timestamp}`
        }
      default:
        return null
    }
  }).filter(Boolean) as Array<{key: string; label: string; value: string; sub?: string; meta: string}>

  return (
    <div className="min-h-screen w-full bg-[#0b0f1a] text-[#e7ecff]">
      <TopNav/>
      <main className="mx-auto max-w-5xl px-4 md:px-6 py-14 md:py-20 space-y-10">
        <header className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-[#7be0b3] uppercase tracking-wide">
            <span className="px-2 py-0.5 rounded bg-[#0b1020] border border-[#1a2236]">sample</span>
            <span>{demoMetricsConfig.note}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold">Open Metrics Dossier (/open)</h1>
          <p className="text-[#b8c4ef] max-w-2xl">
            Laurel publishes sample momentum data to demonstrate how portfolio proof cards render without exposing customer secrets. Pull these views into your own workspace once you connect real integrations.
          </p>
        </header>
        <section className="grid md:grid-cols-2 gap-6">
          <div className="card rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium">Momentum Snapshot</h2>
              <span className="text-[10px] px-2 py-0.5 rounded bg-[#0b1020] border border-[#1a2236] text-[#7be0b3]">sample</span>
            </div>
            <div className="grid gap-3">
              {metrics.map((metric) => (
                <Metric
                  key={metric.key}
                  label={metric.label}
                  value={metric.value}
                  sub={metric.sub}
                  sample
                  meta={metric.meta}
                />
              ))}
            </div>
          </div>
          <div className="card rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium">MRR Trajectory (90d)</h2>
              <span className="text-[10px] px-2 py-0.5 rounded bg-[#0b1020] border border-[#1a2236] text-[#7be0b3]">sample</span>
            </div>
            <div className="text-sm text-[#8da0d0]">Sources: Stripe • GA4 • Founder journal</div>
            <Sparkline data={demoMetricsConfig.sparklines.mrr_90d} height={140}/>
            <div className="text-[11px] text-[#5e6b92]">Updated {timestamp}</div>
          </div>
        </section>
        <section className="card rounded-2xl p-6 space-y-3 text-sm text-[#b8c4ef]">
          <div className="text-xs text-[#8da0d0] uppercase tracking-wide">How to read this</div>
          <p>
            Directional valuation bands land once Laurel ingests your real MRR, churn, and customer data. Expect proofs to refresh weekly (faster when you opt in to Digest boosts). Partner-ready briefs respect warm-intro gates so you never overpromise momentum.
          </p>
          <p className="text-[11px] text-[#5e6b92]">
            Want your dossier unlocked? Generate your valuation scorecard, connect real sources, and raise Fit ≥75 / Readiness ≥70 to open warm investor paths.
          </p>
        </section>
      </main>
      <Footer/>
    </div>
  )
}
